import { load } from 'cheerio';
import { useActiveUrlStore } from '@/store/activeUrlStore';
import { useUrlsStore } from '@/store/urlsStore';
import { useUrlSourcesStore } from '@/store/urlSourcesStore';
import UrlFetchStatus from '@/enums/UrlFetchStatus';
import Provider from '@/enums/Provider';
import determineUrlType from '@/helpers/urlType/determineUrlType';
import IUrl from '@/interfaces/IUrl';
import { useNotificationStore } from '@/store/notificationStore';
import { GoogleAnalytics } from '@/services/analytics/googleAnalytics';

export function extractUrls(htmlSource: string) {
    const activeUrlStore = useActiveUrlStore();
    const urlsStore = useUrlsStore();
    const notificationStore = useNotificationStore();
    const baseUrl = activeUrlStore.activeUrl;
    const provider = activeUrlStore.getProvider;

    // Normalize the base hostname by removing the "www." prefix
    const baseHostname = new URL(baseUrl).hostname.replace(/^www\./, '');

    switch (provider) {
        case Provider.KBS:
            break;
        case Provider.IO: {
            // Load the HTML
            const $ = load(htmlSource);
            const elements = $('[href]');
            const extractedUrls: string[] = [];

            elements.each((_, el) => {
                const href = $(el).attr('href');
                if (!href) return;

                // Skip protocol-relative (starts with //)
                if (href.startsWith('//')) return;

                // Skip tel: & mailto: or other external link schemes
                const lowerHref = href.trim().toLowerCase();
                if (lowerHref.startsWith('tel:')) return;
                if (lowerHref.startsWith('mailto:')) return;

                // We can also skip known WP resource references if you do not want them
                // (Alternatively, you can keep them, but they'll just be typed as OTHER later.)
                // For example:
                if (
                    lowerHref.includes('wp-content') ||
                    lowerHref.includes('wp-json') ||
                    lowerHref.includes('uploads') ||
                    lowerHref.endsWith('.css') ||
                    lowerHref.endsWith('.js') ||
                    lowerHref.endsWith('.jpg') ||
                    lowerHref.endsWith('.png') ||
                    lowerHref.endsWith('.pdf')
                ) {
                    return;
                }

                // Attempt to form a full URL
                let fullUrl: string;
                try {
                    const urlObj = new URL(href, baseUrl);
                    // force https
                    urlObj.protocol = 'https:';
                    fullUrl = urlObj.toString();
                } catch (error) {
                    notificationStore.addErrorNotification(`Error processing URL: ${href} - ${error}`);
                    return;
                }

                // Compare domain to skip external domains
                const currentHostname = new URL(fullUrl).hostname.replace(/^www\./, '');
                if (currentHostname === baseHostname) {
                    extractedUrls.push(fullUrl);
                }
            });

            // Deduplicate by pathname, classify, and add to store
            const urlObjects: IUrl[] = [];
            const seenPathnames = new Set<string>();

            extractedUrls.forEach((url) => {
                try {
                    const urlObj = new URL(url);
                    const pathname = urlObj.pathname;
                    if (seenPathnames.has(pathname)) return;
                    seenPathnames.add(pathname);

                    const urlType = determineUrlType(url, Provider.IO);
                    const addedUrl: IUrl = {
                        pathname,
                        urlType,
                        isSitemap: false,
                        fetchStatus: UrlFetchStatus.TO_FETCH,
                    };
                    urlObjects.push(addedUrl);
                } catch (error) {
                    notificationStore.addErrorNotification(`Error processing URL: ${url} - ${error}`);
                }
            });

            urlObjects.forEach((obj) => urlsStore.addUrlObject(obj));
            break;
        }
        case Provider.ERS: {
            // Load the HTML using Cheerio
            const $ = load(htmlSource);

            // Select any element with an href attribute
            const elements = $('[href]');
            const extractedUrls: string[] = [];

            elements.each((_, el) => {
                const href = $(el).attr('href');
                if (!href) return;
                // Skip protocol-relative URLs (e.g., //example.com)
                if (href.startsWith('//')) return;
                // Ignore URLs containing /cp/ resources
                if (href.includes('/cp/')) return;
                // Skip tel: URLs (or any other schemes you wish to ignore)
                if (href.trim().toLowerCase().startsWith('tel:')) return;

                // Create a full URL using the base URL as a fallback for relative URLs
                let fullUrl;
                try {
                    // Create a URL object from href with baseUrl fallback
                    const urlObj = new URL(href, baseUrl);
                    // Enforce HTTPS protocol for security reasons
                    urlObj.protocol = 'https:';
                    fullUrl = urlObj.toString();
                } catch (error) {
                    notificationStore.addErrorNotification(`Error processing URL: ${href} - ${error}`);
                    return;
                }

                // Normalize the current URL's hostname for comparison
                const currentHostname = new URL(fullUrl).hostname.replace(/^www\./, '');
                if (currentHostname === baseHostname) {
                    extractedUrls.push(fullUrl);
                }
            });

            // Process the URLs to create sitemap URL objects.
            // We won’t have lastmod, changefreq, or priority from the homepage—set them to empty.
            const urlObjects: IUrl[] = [];
            const seenPathnames = new Set<string>();

            extractedUrls.forEach((url) => {
                try {
                    const urlObj = new URL(url);
                    const pathname = urlObj.pathname;
                    if (seenPathnames.has(pathname)) return;
                    seenPathnames.add(pathname);
                    // Determine the URL type (your helper returns a UrlTypes value)
                    const urlType = determineUrlType(url, activeUrlStore.getProvider);
                    const addedUrl: IUrl = {
                        pathname,
                        urlType,
                        isSitemap: false,
                        fetchStatus: UrlFetchStatus.TO_FETCH,
                    };
                    urlObjects.push(addedUrl);
                } catch (error) {
                    notificationStore.addErrorNotification(`Error processing URL: ${url} - ${error}`);
                }
            });

            // Add each URL object to the sitemap store.
            // The store’s updateFetchStatus action already skips duplicates based on pathname.
            urlObjects.forEach((obj) => urlsStore.addUrlObject(obj));
            break;
        }
    }
}

export function extractHomePageUrl() {
    GoogleAnalytics.sitemapGenerate();
    const urlSourcesStore = useUrlSourcesStore();
    extractUrls(urlSourcesStore.getHomepageSource || '');
}
